/*
 * Asset search: matching and ranking.
 *
 * The same module lives in bam-mp (src/dux/search/utils.js); keep the two in
 * step.
 *
 * Two problems this replaces:
 *
 *  1. Search text was compiled straight into a RegExp. Typing "1.8" matched
 *     the pattern /1.8/ -- "1", any character, "8" -- which is found in nearly
 *     every asset's ids and timestamps. On abs it matched 121 of 229 assets,
 *     and the two products actually named "1.8 GHz" landed past the display
 *     cap. A query containing ( [ * + or ? threw "Invalid regular expression".
 *
 *  2. Results had no ranking. A single-token search returned assets in object
 *     iteration order, so a title match could sit behind thirty filename
 *     matches. Now every match is scored by where it was found, in this order
 *     of preference:
 *
 *        title > filename > tags > description > any other metadata
 *
 *     and multi-token searches keep their existing "more tokens matched ranks
 *     higher" behaviour on top of that.
 */

/** Escape a user-typed token so RegExp treats every character literally. */
export const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * The fields a match is ranked by, best first. Anything not listed -- ids,
 * urls, keys, timestamps -- counts as "other metadata" and ranks last.
 */
const RANKED_FIELDS = [
  { name: 'title', tier: 0 },
  { name: 'filename', tier: 1 },
  { name: 'tags', tier: 2 },
  { name: 'description', tier: 3 },
]
const OTHER_METADATA_TIER = RANKED_FIELDS.length

/**
 * Where a token matches an asset: the tier of the best-ranked field that
 * contains it, or -1 when it appears nowhere. `json` is the asset's serialized
 * form, passed in so it is built once per asset rather than once per token.
 */
export const matchTier = (asset, regex, json) => {
  for (const field of RANKED_FIELDS) {
    const value = asset[field.name]
    if (Array.isArray(value)) {
      if (value.some((item) => regex.test(String(item)))) return field.tier
    } else if (value != null && regex.test(String(value))) {
      return field.tier
    }
  }
  return regex.test(json) ? OTHER_METADATA_TIER : -1
}

/**
 * Rank assets against search tokens.
 *
 * @param {Array<[string, object]>} assets  [assetId, assetData] pairs
 * @param {string[]} tokens                 cleaned search tokens
 * @param {{ requireAll?: boolean }} options requireAll = every token must
 *                                          match (an "and" search)
 * @returns {string[]} matching asset ids, best first
 *
 * Ordering, most significant first:
 *   1. number of tokens matched (more is better)
 *   2. best tier across the matched tokens (title beats filename, etc.)
 *   3. sum of tiers (an asset matching two tokens in the title beats one
 *      matching them in title + description)
 *   4. original order, as a stable tiebreak
 */
export const rankAssets = (assets, tokens, options = {}) => {
  const requireAll = !!options.requireAll
  const regexes = tokens.map((token) => new RegExp(escapeRegex(token), 'i'))
  if (regexes.length === 0) return []

  const scored = []
  for (const [assetId, assetData] of assets) {
    const json = JSON.stringify(assetData)
    const tiers = []
    for (const regex of regexes) {
      const tier = matchTier(assetData, regex, json)
      if (tier >= 0) tiers.push(tier)
    }
    if (tiers.length === 0) continue
    if (requireAll && tiers.length < regexes.length) continue

    scored.push({
      assetId,
      matched: tiers.length,
      best: Math.min(...tiers),
      total: tiers.reduce((sum, tier) => sum + tier, 0),
    })
  }

  scored.sort(
    (a, b) => b.matched - a.matched || a.best - b.best || a.total - b.total,
  )
  return scored.map((entry) => entry.assetId)
}
